import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Sparkles, Loader2, CheckCircle2, AlertCircle, Film, Trash2, Download,
  Star, StarOff, Users, Clock, Wand2, MessageCircle, Cake, BookOpen, Pencil,
} from "lucide-react";

const MODES = [
  { key: "talking_clip", label: "Talking Clip", icon: MessageCircle, hint: "A Sea Star says a line directly to camera." },
  { key: "story_scene",  label: "Story Scene",  icon: BookOpen,     hint: "Describe an action — AI animates the cast doing it." },
  { key: "birthday_gift", label: "Birthday",     icon: Cake,         hint: "Personalized happy-birthday clip with a child's name." },
  { key: "open",          label: "Open Prompt",  icon: Pencil,       hint: "Free-form prompt for experimentation." },
];

const STYLES = [
  { key: "watercolor", label: "Watercolor",  swatch: "linear-gradient(135deg,#f0a988,#7fcfc7)" },
  { key: "pixar",      label: "3D Pixar",    swatch: "linear-gradient(135deg,#9bcd9e,#5a8a6f)" },
  { key: "anime",      label: "Anime / Ghibli", swatch: "linear-gradient(135deg,#a3d4ed,#b9a3ed)" },
  { key: "claymation", label: "Claymation",  swatch: "linear-gradient(135deg,#d99b71,#a36b29)" },
  { key: "mixed",      label: "Mixed Media", swatch: "linear-gradient(135deg,#f5d063,#f0a988)" },
];

const DURATIONS = [4, 8, 12];
const SIZES = [
  { v: "1280x720", l: "16:9 HD" },
  { v: "1792x1024", l: "Widescreen" },
  { v: "1024x1792", l: "Portrait" },
  { v: "1024x1024", l: "Square" },
];

const STATUS_LABEL = {
  queued: "Queued",
  building_prompt: "Writing prompt",
  rendering: "Rendering",
  saving: "Saving",
  done: "Done",
  failed: "Failed",
};

export default function AdminVideos() {
  const [mode, setMode] = useState("talking_clip");
  const [title, setTitle] = useState("");
  const [characters, setCharacters] = useState([]);
  const [selectedSlugs, setSelectedSlugs] = useState(["myrtle"]);
  const [dialogue, setDialogue] = useState("Welcome to Stingray Cay!");
  const [sceneText, setSceneText] = useState("");
  const [childName, setChildName] = useState("");
  const [style, setStyle] = useState("watercolor");
  const [size, setSize] = useState("1280x720");
  const [duration, setDuration] = useState(8);
  const [model, setModel] = useState("sora-2");
  const [showOnHomepage, setShowOnHomepage] = useState(false);

  const [videos, setVideos] = useState([]);
  const [job, setJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [costSummary, setCostSummary] = useState({ spent: 0, cap: null });
  const pollRef = useRef(null);

  useEffect(() => {
    api.get("/characters").then(({ data }) => setCharacters(data || [])).catch(() => {});
    refresh();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/videos");
      setVideos(data.videos || []);
      setCostSummary({ spent: data.month_to_date_cost_usd || 0, cap: data.monthly_cap_usd });
    } catch { /* non-fatal */ }
  };

  const toggleSlug = (slug) => {
    setSelectedSlugs((p) => p.includes(slug) ? p.filter((s) => s !== slug) : (p.length >= 4 ? p : [...p, slug]));
  };

  const startPoll = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/videos/status/${jobId}`);
        setJob(data);
        if (data.status === "done" || data.status === "failed") {
          clearInterval(pollRef.current); pollRef.current = null;
          refresh();
          if (data.status === "done") toast.success("Video ready!");
          if (data.status === "failed") toast.error(data.error || "Render failed");
        }
      } catch { /* keep polling */ }
    }, 5000);
  };

  const validate = () => {
    if (mode === "talking_clip" && !dialogue.trim()) return "Add a line of dialogue.";
    if (mode === "story_scene" && !sceneText.trim()) return "Describe the scene.";
    if (mode === "birthday_gift" && !childName.trim()) return "Enter the child's name.";
    if (mode === "open" && !sceneText.trim()) return "Add a prompt.";
    if (!selectedSlugs.length) return "Pick at least one Sea Star.";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/videos/generate", {
        mode, title: title.trim() || undefined,
        character_slugs: selectedSlugs,
        dialogue: dialogue.trim() || undefined,
        scene_text: sceneText.trim() || undefined,
        child_name: childName.trim() || undefined,
        style, size, duration, model,
        show_on_homepage: showOnHomepage,
      });
      setJob({ id: data.job_id, status: "queued", progress: 0, step: "Queued" });
      startPoll(data.job_id);
      toast.success("Sora is rendering — usually 2–5 minutes.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start.");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this video permanently?")) return;
    try { await api.delete(`/admin/videos/${id}`); toast.success("Deleted"); refresh(); }
    catch { toast.error("Delete failed"); }
  };

  const toggleFeature = async (id) => {
    try { await api.post(`/admin/videos/${id}/feature`); refresh(); }
    catch { toast.error("Toggle failed"); }
  };

  const download = async (v) => {
    try {
      const { data } = await api.get(`/admin/videos/${v.id}/download`, { responseType: "blob" });
      const dlUrl = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = dlUrl;
      link.download = v.filename || `${v.title}.mp4`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(dlUrl), 1000);
    } catch { toast.error("Download failed"); }
  };

  const isRunning = job && job.status !== "done" && job.status !== "failed";
  const estimatedCost = ((model === "sora-2" ? 0.10 : 0.30) * duration).toFixed(2);
  const capPct = costSummary.cap ? Math.min(100, Math.round((costSummary.spent / costSummary.cap) * 100)) : 0;

  return (
    <div className="space-y-5" data-testid="admin-videos-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-accent text-3xl font-bold text-[#2e3a3a] flex items-center gap-2">
            <Film className="w-7 h-7 text-[#7fcfc7]" /> Sea Star Studio
          </h1>
          <p className="text-sm text-[#5a6b76] mt-1">Make AI videos of your Sea Stars with Sora 2 — talking clips, story scenes, and birthday gifts.</p>
        </div>
        {costSummary.cap != null && (
          <div className="bg-white border-2 border-[#f4e4c6] rounded-2xl px-4 py-2 text-xs" data-testid="video-budget">
            <div className="flex items-center gap-2 mb-1"><Clock className="w-3 h-3 text-[#5a6b76]" /><span className="font-bold">Month-to-date</span></div>
            <div className="text-[#3a4a55] text-sm font-bold">${costSummary.spent.toFixed(2)} <span className="text-[#5a6b76] font-normal">/ ${costSummary.cap.toFixed(2)}</span></div>
            <div className="w-32 h-1.5 bg-[#f4e4c6] rounded-full mt-1 overflow-hidden">
              <div className={`h-full ${capPct > 80 ? "bg-red-400" : "bg-[#7fcfc7]"}`} style={{ width: `${capPct}%` }} />
            </div>
          </div>
        )}
      </header>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* FORM */}
        <section className="lg:col-span-2 bg-white border-2 border-[#f4e4c6] rounded-3xl p-5 space-y-4">
          {/* Mode tabs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="video-mode-tabs">
            {MODES.map((m) => {
              const Icon = m.icon;
              const on = mode === m.key;
              return (
                <button key={m.key} onClick={() => setMode(m.key)}
                  className={`p-3 rounded-2xl border-2 text-left transition ${on ? "bg-[#eef9fb] border-[#7fcfc7]" : "bg-[#fffbf3] border-[#f4e4c6] hover:border-[#7fcfc7]"}`}
                  data-testid={`video-mode-${m.key}`}>
                  <Icon className={`w-5 h-5 mb-1 ${on ? "text-[#236f6b]" : "text-[#5a6b76]"}`} />
                  <div className="font-bold text-sm text-[#2e3a3a]">{m.label}</div>
                  <div className="text-[10px] text-[#5a6b76]">{m.hint}</div>
                </button>
              );
            })}
          </div>

          {/* Mode-specific inputs */}
          {mode === "talking_clip" && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#5a6b76]">Dialogue</label>
              <textarea value={dialogue} onChange={(e) => setDialogue(e.target.value)} rows={2}
                placeholder="What does the Sea Star say?" maxLength={240}
                className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm"
                data-testid="video-dialogue" />
            </div>
          )}
          {(mode === "story_scene" || mode === "open") && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#5a6b76]">
                {mode === "story_scene" ? "Scene description" : "Open prompt"}
              </label>
              <textarea value={sceneText} onChange={(e) => setSceneText(e.target.value)} rows={3}
                placeholder={mode === "story_scene" ? "Ray surfs a tall wave while Casey waves a flag from the shore." : "Whatever you can dream up."}
                maxLength={600}
                className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm"
                data-testid="video-scene" />
            </div>
          )}
          {mode === "birthday_gift" && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#5a6b76]">Child's name</label>
              <input value={childName} onChange={(e) => setChildName(e.target.value)} maxLength={60}
                placeholder="e.g. Mila" className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm"
                data-testid="video-child-name" />
            </div>
          )}

          {/* Cast */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#5a6b76] flex items-center gap-1">
              <Users className="w-3.5 h-3.5" /> Cast ({selectedSlugs.length} / 4)
            </label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {characters.map((c) => {
                const on = selectedSlugs.includes(c.slug);
                return (
                  <button key={c.slug} onClick={() => toggleSlug(c.slug)}
                    className={`text-xs px-2.5 py-1 rounded-full border-2 transition ${on ? "bg-[#7fcfc7] border-[#236f6b] text-white" : "bg-white border-[#f4e4c6] text-[#3a4a55] hover:border-[#7fcfc7]"}`}
                    data-testid={`video-cast-${c.slug}`}>
                    {c.name?.split(" the ")[0] || c.name || c.slug}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Style swatches */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#5a6b76]">Style</label>
            <div className="mt-2 grid grid-cols-3 sm:grid-cols-5 gap-2">
              {STYLES.map((s) => (
                <button key={s.key} onClick={() => setStyle(s.key)}
                  className={`text-xs p-2 rounded-2xl border-2 transition ${style === s.key ? "border-[#7fcfc7] bg-[#eef9fb]" : "border-[#f4e4c6] bg-white hover:border-[#7fcfc7]"}`}
                  data-testid={`video-style-${s.key}`}>
                  <div className="w-full h-8 rounded-lg mb-1" style={{ background: s.swatch }} />
                  <div className="font-bold text-[#3a4a55]">{s.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Title + technical */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#5a6b76]">Title (optional)</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
                placeholder="e.g. Myrtle Welcomes Campers"
                className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm"
                data-testid="video-title" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#5a6b76]">Quality</label>
              <select value={model} onChange={(e) => setModel(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm"
                data-testid="video-model">
                <option value="sora-2">Sora 2 — Standard (~$0.10/s)</option>
                <option value="sora-2-pro">Sora 2 Pro — Premium (~$0.30/s)</option>
              </select>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#5a6b76]">Duration</label>
              <div className="mt-1 flex gap-1">
                {DURATIONS.map((d) => (
                  <button key={d} onClick={() => setDuration(d)}
                    className={`flex-1 py-2 rounded-xl border-2 text-sm font-bold ${duration === d ? "bg-[#7fcfc7] border-[#236f6b] text-white" : "bg-white border-[#f4e4c6] text-[#3a4a55] hover:border-[#7fcfc7]"}`}
                    data-testid={`video-duration-${d}`}>{d}s</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#5a6b76]">Size</label>
              <select value={size} onChange={(e) => setSize(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-[#f4e4c6] bg-[#fffbf3] focus:border-[#7fcfc7] outline-none text-sm"
                data-testid="video-size">
                {SIZES.map((s) => <option key={s.v} value={s.v}>{s.l} ({s.v})</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#5a6b76]">Homepage</label>
              <button type="button" onClick={() => setShowOnHomepage((v) => !v)}
                className={`mt-1 w-full h-[42px] px-3 rounded-xl border-2 text-sm font-bold ${showOnHomepage ? "bg-[#fff4d6] border-[#f0a988] text-[#a36b29]" : "bg-[#fffbf3] border-[#f4e4c6] text-[#5a6b76]"}`}
                data-testid="video-homepage-toggle">
                {showOnHomepage ? "★ Featured" : "Library only"}
              </button>
            </div>
          </div>

          <div className="pt-2 border-t border-[#f4e4c6] flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-[#5a6b76]">⏱ ~2–5 min · 💰 ~${estimatedCost} per clip</div>
            <button onClick={submit} disabled={submitting || isRunning}
              className="btn-primary inline-flex disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="video-submit">
              {submitting || isRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {isRunning ? "Rendering…" : "Generate video"}
            </button>
          </div>
        </section>

        {/* PROGRESS + LIBRARY PREVIEW */}
        <aside className="space-y-3">
          {job && (
            <div className="bg-white border-2 border-[#7fcfc7] rounded-3xl p-4 space-y-2" data-testid="video-progress">
              <div className="flex items-center gap-2">
                {job.status === "done" ? <CheckCircle2 className="w-5 h-5 text-[#5a8a6f]" />
                  : job.status === "failed" ? <AlertCircle className="w-5 h-5 text-red-600" />
                  : <Loader2 className="w-5 h-5 animate-spin text-[#7fcfc7]" />}
                <div className="font-bold text-[#2e3a3a]">{STATUS_LABEL[job.status] || job.status}</div>
              </div>
              <div className="text-xs text-[#5a6b76]">{job.step || ""}</div>
              {typeof job.progress === "number" && (
                <div className="h-2 bg-[#f4e4c6] rounded-full overflow-hidden">
                  <div className="h-full bg-[#7fcfc7] transition-all" style={{ width: `${Math.min(100, Math.max(0, job.progress))}%` }} />
                </div>
              )}
              {job.status === "done" && job.video_url && (
                <video src={job.video_url} controls className="w-full rounded-xl border border-[#f4e4c6] mt-2" />
              )}
              {job.error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl p-2">{job.error}</div>}
            </div>
          )}
          <div className="bg-[#fff4d6] border-2 border-[#f0a988] rounded-3xl p-4 text-xs text-[#a36b29] space-y-1">
            <div className="font-bold flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Tips</div>
            <ul className="list-disc ml-4 space-y-0.5">
              <li>Pick 1 character for talking clips; up to 4 for scenes.</li>
              <li>Sora 2 Standard is 3× cheaper and usually plenty good for kids' content.</li>
              <li>Star a video to feature it on the homepage hero rotation.</li>
              <li>Generated clips are MP4 — drop them anywhere (social, email, story quest).</li>
            </ul>
          </div>
        </aside>
      </div>

      {/* LIBRARY */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-accent text-2xl font-bold text-[#2e3a3a]">Library ({videos.length})</h2>
        </div>
        {videos.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-[#f4e4c6] rounded-3xl py-12 text-center">
            <Film className="w-8 h-8 mx-auto text-[#7fcfc7] mb-2" />
            <p className="font-accent text-xl text-[#3a4a55]">No videos yet</p>
            <p className="text-sm text-[#5a6b76] mt-1">Generate your first one above.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="video-library">
            {videos.map((v) => (
              <div key={v.id} className="bg-white border-2 border-[#f4e4c6] rounded-3xl overflow-hidden" data-testid={`video-card-${v.id}`}>
                <video src={v.url} controls className="w-full aspect-video bg-black" preload="metadata" />
                <div className="p-3 space-y-1">
                  <div className="font-bold text-sm text-[#2e3a3a] line-clamp-1">{v.title}</div>
                  <div className="text-[10px] text-[#5a6b76]">{v.mode.replace("_", " ")} · {v.duration}s · {v.style}</div>
                  <div className="flex gap-1 pt-1">
                    <button onClick={() => download(v)} className="btn-ghost text-xs flex-1 justify-center" data-testid={`video-dl-${v.id}`}>
                      <Download className="w-3 h-3" /> MP4
                    </button>
                    <button onClick={() => toggleFeature(v.id)} title={v.show_on_homepage ? "Remove from homepage" : "Feature on homepage"}
                      className={`text-xs px-2 py-1.5 rounded-full ${v.show_on_homepage ? "text-[#a36b29] bg-[#fff4d6]" : "text-[#5a6b76] hover:bg-[#fffbf3]"}`}
                      data-testid={`video-feature-${v.id}`}>
                      {v.show_on_homepage ? <Star className="w-3 h-3 fill-current" /> : <StarOff className="w-3 h-3" />}
                    </button>
                    <button onClick={() => remove(v.id)} className="text-xs px-2 py-1.5 rounded-full text-red-700 hover:bg-red-50" data-testid={`video-del-${v.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
